import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Tag, Trash2, ToggleLeft, ToggleRight, Plus, Edit2, X } from 'lucide-react';
import { toast } from 'sonner';
import { ALL_PLANS } from '../config/plans';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminCouponManagement = () => {
  const [coupons, setCoupons] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [editingCoupon, setEditingCoupon] = useState(null);
  const [newCoupon, setNewCoupon] = useState({
    code: '',
    discount_percentage: '',
    valid_plans: [],
    expiry_date: '',
    max_uses: ''
  });

  useEffect(() => {
    fetchCoupons();
  }, []);

  const fetchCoupons = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/admin/coupons/list`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data.success) {
        setCoupons(response.data.coupons);
      }
    } catch (error) {
      toast.error('Failed to fetch coupons');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateCoupon = async () => {
    if (!newCoupon.code || !newCoupon.discount_percentage) {
      toast.error('Please fill required fields');
      return;
    }

    if (newCoupon.valid_plans.length === 0) {
      toast.error('Please select at least one plan');
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const payload = {
        code: newCoupon.code.toUpperCase(),
        discount_percentage: parseInt(newCoupon.discount_percentage),
        valid_plans: newCoupon.valid_plans,
        expiry_date: newCoupon.expiry_date || null,
        max_uses: newCoupon.max_uses ? parseInt(newCoupon.max_uses) : null
      };

      const response = await axios.post(`${API}/admin/coupons/create`, payload, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data.success) {
        toast.success('Coupon created successfully');
        setNewCoupon({
          code: '',
          discount_percentage: '',
          valid_plans: [],
          expiry_date: '',
          max_uses: ''
        });
        setShowCreateForm(false);
        fetchCoupons();
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create coupon');
    }
  };

  const handleEditCoupon = async () => {
    if (!editingCoupon.code || !editingCoupon.discount_percentage) {
      toast.error('Please fill required fields');
      return;
    }

    if (editingCoupon.valid_plans.length === 0) {
      toast.error('Please select at least one plan');
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const payload = {
        code: editingCoupon.code.toUpperCase(),
        discount_percentage: parseInt(editingCoupon.discount_percentage),
        valid_plans: editingCoupon.valid_plans,
        expiry_date: editingCoupon.expiry_date || null,
        max_uses: editingCoupon.max_uses ? parseInt(editingCoupon.max_uses) : null
      };

      const response = await axios.put(
        `${API}/admin/coupons/${editingCoupon.id}/edit`,
        payload,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.success) {
        toast.success('Coupon updated successfully');
        setEditingCoupon(null);
        fetchCoupons();
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update coupon');
    }
  };

  const handleToggleCoupon = async (couponId) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.put(
        `${API}/admin/coupons/${couponId}/toggle`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.success) {
        toast.success(`Coupon ${response.data.active ? 'activated' : 'deactivated'}`);
        fetchCoupons();
      }
    } catch (error) {
      toast.error('Failed to toggle coupon');
    }
  };

  const handleDeleteCoupon = async (couponId) => {
    if (!window.confirm('Are you sure you want to delete this coupon?')) {
      return;
    }

    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/admin/coupons/${couponId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      toast.success('Coupon deleted successfully');
      fetchCoupons();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete coupon');
    }
  };

  const handlePlanToggle = (planId, isForEdit = false) => {
    const target = isForEdit ? editingCoupon : newCoupon;
    const setter = isForEdit ? setEditingCoupon : setNewCoupon;

    if (planId === 'all') {
      // Toggle "all" - if selecting all, clear other selections
      const newPlans = target.valid_plans.includes('all') ? [] : ['all'];
      setter({ ...target, valid_plans: newPlans });
    } else {
      // Toggle individual plan
      let newPlans = [...target.valid_plans].filter(p => p !== 'all'); // Remove "all" if present
      if (newPlans.includes(planId)) {
        newPlans = newPlans.filter(p => p !== planId);
      } else {
        newPlans.push(planId);
      }
      setter({ ...target, valid_plans: newPlans });
    }
  };

  const renderCouponForm = (coupon, isEdit = false) => {
    const data = isEdit ? editingCoupon : newCoupon;
    const handleSave = isEdit ? handleEditCoupon : handleCreateCoupon;
    const handleCancel = () => isEdit ? setEditingCoupon(null) : setShowCreateForm(false);

    return (
      <div className="bg-gray-50 p-4 rounded-lg mb-6 space-y-4">
        <div className="flex items-center justify-between">
          <h3 className="font-semibold">{isEdit ? 'Edit Coupon' : 'Create New Coupon'}</h3>
          <Button size="sm" variant="ghost" onClick={handleCancel}>
            <X className="h-4 w-4" />
          </Button>
        </div>
        
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium mb-1">Coupon Code *</label>
            <Input
              value={data.code}
              onChange={(e) => {
                if (isEdit) {
                  setEditingCoupon({ ...data, code: e.target.value.toUpperCase() });
                } else {
                  setNewCoupon({ ...data, code: e.target.value.toUpperCase() });
                }
              }}
              placeholder="SAVE20"
              maxLength={20}
            />
          </div>

          <div>
            <label className="block text-sm font-medium mb-1">Discount (%) *</label>
            <Input
              type="number"
              value={data.discount_percentage}
              onChange={(e) => {
                if (isEdit) {
                  setEditingCoupon({ ...data, discount_percentage: e.target.value });
                } else {
                  setNewCoupon({ ...data, discount_percentage: e.target.value });
                }
              }}
              placeholder="10"
              min="1"
              max="100"
            />
          </div>

          <div className="col-span-2">
            <label className="block text-sm font-medium mb-2">Valid For Plans *</label>
            <div className="grid grid-cols-2 gap-2 max-h-60 overflow-y-auto border rounded p-3">
              <label className="flex items-center gap-2 cursor-pointer hover:bg-gray-100 p-2 rounded">
                <input
                  type="checkbox"
                  checked={data.valid_plans.includes('all')}
                  onChange={() => handlePlanToggle('all', isEdit)}
                  className="w-4 h-4"
                />
                <span className="text-sm font-semibold">All Plans</span>
              </label>
              {ALL_PLANS.map(plan => (
                <label key={plan.id} className="flex items-center gap-2 cursor-pointer hover:bg-gray-100 p-2 rounded">
                  <input
                    type="checkbox"
                    checked={data.valid_plans.includes(plan.id)}
                    onChange={() => handlePlanToggle(plan.id, isEdit)}
                    disabled={data.valid_plans.includes('all')}
                    className="w-4 h-4"
                  />
                  <span className="text-sm">{plan.name}</span>
                </label>
              ))}
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium mb-1">Max Uses (Optional)</label>
            <Input
              type="number"
              value={data.max_uses}
              onChange={(e) => {
                if (isEdit) {
                  setEditingCoupon({ ...data, max_uses: e.target.value });
                } else {
                  setNewCoupon({ ...data, max_uses: e.target.value });
                }
              }}
              placeholder="100"
              min="1"
            />
          </div>

          <div>
            <label className="block text-sm font-medium mb-1">Expiry Date (Optional)</label>
            <Input
              type="date"
              value={data.expiry_date}
              onChange={(e) => {
                if (isEdit) {
                  setEditingCoupon({ ...data, expiry_date: e.target.value });
                } else {
                  setNewCoupon({ ...data, expiry_date: e.target.value });
                }
              }}
            />
          </div>
        </div>

        <Button onClick={handleSave} className="w-full">
          {isEdit ? 'Update Coupon' : 'Create Coupon'}
        </Button>
      </div>
    );
  };

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle className="flex items-center gap-2">
            <Tag className="h-5 w-5" />
            Coupon Management
          </CardTitle>
          {!showCreateForm && !editingCoupon && (
            <button
              onClick={() => setShowCreateForm(true)}
              className="px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg flex items-center gap-2"
            >
              <Plus className="h-4 w-4" />
              Create Coupon
            </button>
          )}
        </div>
      </CardHeader>
      <CardContent>
        {/* Create Form */}
        {showCreateForm && renderCouponForm(null, false)}

        {/* Edit Form */}
        {editingCoupon && renderCouponForm(editingCoupon, true)}

        {/* Coupons List */}
        {loading ? (
          <div className="text-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600 mx-auto"></div>
          </div>
        ) : coupons.length === 0 ? (
          <p className="text-center text-gray-500 py-8">No coupons created yet</p>
        ) : (
          <div className="space-y-3">
            {coupons.map((coupon) => (
              <div
                key={coupon.id}
                className={`border rounded-lg p-4 ${
                  coupon.active ? 'bg-white' : 'bg-gray-100'
                }`}
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <span className="font-mono font-bold text-lg">{coupon.code}</span>
                      <span className="px-2 py-1 bg-green-100 text-green-700 rounded text-sm font-semibold">
                        {coupon.discount_percentage}% OFF
                      </span>
                      {!coupon.active && (
                        <span className="px-2 py-1 bg-gray-200 text-gray-600 rounded text-xs">
                          Inactive
                        </span>
                      )}
                    </div>

                    <div className="text-sm text-gray-600 space-y-1">
                      <p>
                        Valid for:{' '}
                        {coupon.valid_plans.includes('all')
                          ? 'All Plans'
                          : coupon.valid_plans.map(pid => {
                              const plan = ALL_PLANS.find(p => p.id === pid);
                              return plan ? plan.name : pid;
                            }).join(', ')}
                      </p>
                      {coupon.max_uses && (
                        <p>
                          Usage: {coupon.current_uses || 0} / {coupon.max_uses}
                        </p>
                      )}
                      {coupon.expiry_date && (
                        <p>
                          Expires: {new Date(coupon.expiry_date).toLocaleDateString()}
                        </p>
                      )}
                    </div>
                  </div>

                  <div className="flex gap-2">
                    <button
                      className="p-2 border border-gray-300 hover:bg-gray-100 rounded"
                      onClick={() => setEditingCoupon(coupon)}
                      title="Edit coupon"
                    >
                      <Edit2 className="h-4 w-4" />
                    </button>
                    <button
                      className="p-2 border border-gray-300 hover:bg-gray-100 rounded"
                      onClick={() => handleToggleCoupon(coupon.id)}
                      title="Toggle active/inactive"
                    >
                      {coupon.active ? (
                        <ToggleRight className="h-4 w-4 text-green-600" />
                      ) : (
                        <ToggleLeft className="h-4 w-4 text-gray-400" />
                      )}
                    </button>
                    <button
                      className="p-2 bg-red-600 hover:bg-red-700 text-white rounded"
                      onClick={() => handleDeleteCoupon(coupon.id)}
                      title="Delete coupon"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default AdminCouponManagement;
